import {
  addHours,
  compareAsc,
  compareDesc,
  differenceInMilliseconds,
  format,
  formatISO,
  isDate,
  isSameDay as isSameDayFn,
  isValid,
  parse,
  parseISO
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export type NullableString = string | null | undefined;

export const defaultLanguageWithLocale = "en-us";
const BE_DATE_FORMAT = "yyyy-MM-dd";

const options: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
};

/**
 * Format the given date to the BE date format.
 * @param date
 * @example
 *    formatDateForBE(new Date(2014, 1, 31));   // 2014-02-31
 */
export const formatDateForBE = (date: Date) => format(date, BE_DATE_FORMAT);

/**
 * Checking if the given value is a date.
 * @param dateString - The date string/undefined/null to check.
 * @example
 *     getDateFormatPattern('null');   // null
 *     getDateFormatPattern('undefined');   // undefined
 *     getDateFormatPattern('2014-02-31');   // 2014-02-32T23:00:00.000Z
 */
export const dateOrNull = (dateString?: NullableString): Date | null | undefined => {
  if (typeof dateString !== "string") {
    return dateString;
  }

  if (dateString === "") {
    return null;
  }

  return new Date(dateString);
};

/**
 * Format the given date range to the given format.
 * @param timeZone
 * @param i18Language
 * @param from
 * @param to
 * @example
 *   formatRange("Europe/Zurich", "en-us", "2021-10-10", "2021-10-11");   // 10/10/2021 - 10/11/2021
 */
export const formatRange = (
  timeZone: string = "Europe/Zurich",
  i18Language: string = defaultLanguageWithLocale,
  from?: string,
  to?: string
): string => {
  const fromDate = dateOrNull(from);
  const toDate = dateOrNull(to);
  if (!fromDate || !toDate) {
    return "";
  }

  return [
    utcToZonedTime(fromDate, timeZone).toLocaleDateString(
      navigator.language ?? i18Language.toLowerCase(),
      options
    ),
    utcToZonedTime(toDate, timeZone).toLocaleDateString(
      navigator.language ?? i18Language.toLowerCase(),
      options
    )
  ].join(" - ");
};

/**
 * Returns the difference between the given date and now in days.
 * @param dateString
 * @example
 *  daysBeforeNow(new Date());   // 0
 *  daysBeforeNow(new Date().setDate(new Date().getDate() - 1));   // 1
 */
export const daysBeforeNow = (dateString?: string): number | "" => {
  const date = dateOrNull(dateString);
  const now = new Date();
  if (!date) {
    return "";
  }

  date.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  // @ts-ignore
  const diffTime = date - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Returns true if the given date is before now. Delta hours can be used to add hours to now.
 * @param dateString
 * @param deltaHours
 * @example
 *  isBeforeNow(new Date());   // false
 *  isBeforeNow(new Date().setDate(new Date().getDate() - 1));   // true
 */
export const isBeforeNow = (dateString?: string, deltaHours: number = 0): boolean => {
  const date = dateOrNull(dateString);
  const now = addHours(new Date(), deltaHours);
  if (!date) {
    return false;
  }

  return differenceInMilliseconds(date, now) < 0;
};

/**
 * Returns true if the given date is the same day as the given date.
 * @param dateLeft
 * @param dateRight
 * @example
 * isSameDay(new Date(), new Date());   // true
 * isSameDay(new Date(), new Date().setDate(new Date().getDate() - 1));   // false
 */
export const isSameDay = (
  dateLeft: Date | number | null,
  dateRight: Date | number | null
): boolean => {
  return dateLeft && dateRight ? isSameDayFn(dateLeft, dateRight) : false;
};

/**
 * Returns true if the given dates are the same days.
 * @param daysLeft
 * @param daysRight
 * @example
 * isSameDaysInArray([new Date(), new Date()], [new Date(), new Date()]);   // true
 * isSameDaysInArray([new Date(), new Date()], [new Date(), new Date().setDate(new Date().getDate() - 1)]);   // false
 */
export const isSameDaysInArray = (daysLeft: Date[], daysRight: Date[]): boolean => {
  if (daysLeft.length !== daysRight.length) {
    return false;
  }

  const left = [...daysLeft].sort(compareAsc);
  const right = [...daysRight].sort(compareAsc);

  for (let i = 0; i < left.length; i++) {
    if (!isSameDay(left[i], right[i])) {
      return false;
    }
  }

  return true;
};

/**
 * Format the passed date based on the given timezone and language.
 * @param dateString
 * @param i18Language
 * @param timeZone
 * @example
 * formatDate("2021-10-10T00:00:00.000Z", "Europe/Zurich", "en-us");   // 10/10/2021
 */
export const formatDate = (
  dateString: NullableString,
  i18Language: string = defaultLanguageWithLocale,
  timeZone: string = "Europe/Zurich"
): NullableString => {
  if (typeof dateString !== "string" || dateString === "") {
    return dateString;
  }

  const date = new Date(dateString);

  return utcToZonedTime(date, timeZone).toLocaleDateString(
    navigator.language ?? i18Language.toLowerCase(),
    options
  );
};

/**
 * Format the passed time based on the given timezone and language.
 * @param dateString
 * @param timeZone
 * @param i18Language
 *
 */
export const formatTime = (
  dateString: NullableString,
  i18Language: string = defaultLanguageWithLocale,
  timeZone: string = "Europe/Zurich"
): NullableString => {
  if (typeof dateString !== "string" || dateString === "") {
    return dateString;
  }

  const date = new Date(dateString);
  return utcToZonedTime(date, timeZone).toLocaleTimeString(
    navigator.language ?? i18Language.toLowerCase(),
    {
      minute: "2-digit",
      hour: "2-digit"
    }
  );
};

/**
 * Format the passed date and time based on the given timezone and language.
 * @param dateString
 * @param i18Language
 * @param timeZone
 */
export const formatDateTime = (
  dateString: NullableString,
  i18Language: string = defaultLanguageWithLocale,
  timeZone: string = "Europe/Zurich"
): NullableString => {
  if (typeof dateString !== "string" || dateString === "") {
    return dateString;
  }

  return `${formatDate(dateString, i18Language, timeZone)} ${formatTime(
    dateString,
    i18Language,
    timeZone
  )}`;
};

// @deprecated Not safe. see first comment of https://stackoverflow.com/a/29774197
// TODO LM-374
/**
 * Format the passed date to the ISO string format.
 * @param date
 * @example
 * formatToIsoString(new Date());   // 2021-10-10T10:10:10.000Z
 * formatToIsoString(new Date().setDate(new Date().getDate() - 1));   // 2021-10-09T10:10:10.000Z
 */
export const formatToIsoDate = (date: Date | undefined | null): string =>
  date ? formatIsoStringToIsoDate(formatISO(date)) : "";

// @deprecated Not safe. see first comment of https://stackoverflow.com/a/29774197
// TODO LM-374
/**
 * Format the passed date string to the ISO date format.
 * @param dateString
 * @example
 * formatToIsoString("2021-10-10T10:10:10.000Z");   // 2021-10-10
 * formatToIsoString("2021-10-09T10:10:10.000Z");   // 2021-10-09
 */
export const formatIsoStringToIsoDate = (dateString: string): string => dateString.split("T")[0];

/**
 * Format the passed date to the ISO date format.
 * @param date
 * @example
 * formatDateToIso(new Date());   // 2021-10-10
 * formatDateToIso(new Date().setDate(new Date().getDate() - 1));   // 2021-10-09
 */
export const formatDateToIso = (date: Date) => formatISO(date, { representation: "date" });

/**
 * Parse the passed date string and return Date object.
 * @param value
 * @example
 * parseIsoDateString("2021-10-10T10:10:10.000Z");   // 2021-10-10T10:10:10.000Z
 */
export const parseIsoDateString = (value: string): Date => {
  const date = parseISO(value);
  return date || null;
};

// TODO LM-374 - fix any if possible - check yup transform return value type and use it
/**
 * If the given value is a date, return it. Otherwise, parse the value to a date.
 * @param originalValue
 * @example
 * parseDateString("2021-10-10T10:10:10.000Z");   // 2021-10-10T10:10:10.000Z
 * parseDateString("2021-10-10");   // 2021-10-10T00:00:00.000Z
 * parseDateString(new Date());   // 2021-10-10T10:10:10.000Z
 * parseDateString(null);   // null
 */
export const parseDateString = (originalValue: any): Date => {
  return isDate(originalValue) ? originalValue : parse(originalValue, "yyyy-MM-dd", new Date());
};

/**
 * Get the date format pattern for the given locale.
 * @param i18Language
 * @example
 *     getDateFormatPattern('en-AU');   // dd/mm/yyyy
 *     getDateFormatPattern('en-US');   // m/d/yyyy
 */
export const dateFormatForLanguage = (i18Language: string = defaultLanguageWithLocale): string => {
  return getDateFormatPattern(navigator.language ?? i18Language.toLowerCase()) || "dd.MM.yyyy";
};

/**
 * Compare given dates.
 * @param firstDate
 * @param secondDate
 * @param ascending
 * @example
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-10T10:10:10.000Z");   // 0
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-11T10:10:10.000Z");   // -1
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-09T10:10:10.000Z");   // 1
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-10T10:10:10.000Z", false);   // 0
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-11T10:10:10.000Z", false);   // 1
 * compareDates("2021-10-10T10:10:10.000Z", "2021-10-09T10:10:10.000Z", false);   // -1
 */
export const compareDates = (firstDate: string, secondDate: string, ascending = true): number => {
  const first = new Date(firstDate);
  const second = new Date(secondDate);
  return ascending ? compareAsc(first, second) : compareDesc(first, second);
};

/**
 * Format the passed date and time based on the given timezone and language.
 * @param date
 * @param time
 * @example
 * formatAndCombineDateTime(new Date(), new Date()); // 2021-10-10T10:10:10.000Z
 * formatAndCombineDateTime(new Date(), new Date().setHours(new Date().getHours() - 1)); // 2021-10-10T09:10:10.000Z
 * formatAndCombineDateTime(new Date(), new Date().setHours(new Date().getHours() + 1)); // 2021-10-10T11:10:10.000Z
 */
export const combineTimeAndDate = (date: Date, time: Date | undefined): Date => {
  if (!time) return date;

  const tempDate = new Date(date);

  tempDate.setHours(time.getHours());
  tempDate.setMinutes(time.getMinutes());
  tempDate.setSeconds(0);
  return tempDate;
};

/**
 * Format the passed time to HH:mm format.
 * @param time
 * @example
 * getTime(new Date());   // 10:10
 * getTime(new Date().setHours(new Date().getHours() - 1)); // 09:10
 * getTime(new Date().setHours(new Date().getHours() + 1)); // 11:10
 * getTime(undefined); // ""
 */
export const getTime = (time: Date | undefined): string => {
  if (time && isValid(time)) {
    return format(time, "HH:mm");
  }
  return "";
};

/**
 * Helpers for date utils
 */

/**
 * Get the date format pattern for the given locale.
 * @example
 *     getDateFormatPattern('en-AU');   // dd/mm/yyyy
 *     getDateFormatPattern('en-US');   // m/d/yyyy
 */
const getDateFormatPattern = (locale: string) => {
  const getPatternForPart = (part: Intl.DateTimeFormatPart) => {
    switch (part.type) {
      case "day":
        return "d".repeat(part.value.length);
      case "month":
        return "M".repeat(part.value.length);
      case "year":
        return "y".repeat(part.value.length);
      case "literal":
        return part.value;
      default:
        console.log("Unsupported date part", part);
        return "";
    }
  };

  // TODO LM-374 what is the purpose of this date ?
  return new Intl.DateTimeFormat(locale)
    .formatToParts(new Date("2021-10-10"))
    .map(getPatternForPart)
    .join("");
};
